import { Injectable } from '@angular/core';
import { createSelector, Store } from '@ngrx/store';
import { Seq } from 'immutable';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { TcsCustomPermission, TcsPermission } from 'src/app/shared/authorization/permissions';
import { EntityType, FetchCardEntitiesGQL } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import {
  AccessibleItemRecord,
  selectAccessibleItemEntitiesByPersonId,
} from 'src/store/generated/accessible-item-record.generated';
import { selectCardSummaryById } from 'src/store/generated/card-summary-record.generated';
import { selectAllCommunicationCenterEntities } from 'src/store/generated/communication-center-record.generated';
import { selectAllControlZoneEntities } from 'src/store/generated/control-zone-record.generated';
import { selectAllRegionEntities } from 'src/store/generated/region-record.generated';
import { selectAllVehicleEntities } from 'src/store/generated/vehicle-record.generated';
import { selectCardByCardNumber, selectCardById } from 'src/store/entityStores/card-selectors';
import { valueOrSwitchMap } from 'src/util/rxjs';
import { StoreService } from 'src/store/store.service';
import { selectRegionByCardId } from 'src/store/entityStores/region-selectors';
import { selectUnitSummaryByCardId } from 'src/store/entityStores/unit-selectors';
import { PermissionsService } from '../amg-authorization/permissions.service';
import { UserService } from '../tcs-identity/user.service';
import { flatMapUserRoles } from './authorization-helpers';

interface CardAccessibleItemEntityIds {
  communicationCenterId?: string;
  controlZoneId?: string;
  companyId?: string;
}

function selectCardSummaryAccessibleItemsForUser({
  userId,
  card: { communicationCenterId, companyId, controlZoneId },
}: {
  userId: string;
  card: CardAccessibleItemEntityIds;
}) {
  return createSelector(selectAccessibleItemEntitiesByPersonId(userId), (accesibleItems) => {
    const ais: AccessibleItemRecord[] = [
      [EntityType.Company, companyId],
      [EntityType.CommunicationCenter, communicationCenterId],
      [EntityType.ControlZone, controlZoneId],
    ]
      .map(([type, id]) => accesibleItems.find((_) => _.type === type && _.entityId === id))
      .filter((ai) => ai != null)
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      .map((_) => _!);

    return Seq.Indexed(ais);
  });
}

function selectCardSummaryAccessibleItemEntityIds(cardId: string) {
  return createSelector(
    selectCardSummaryById(cardId),
    selectAllVehicleEntities,
    selectAllControlZoneEntities,
    selectAllCommunicationCenterEntities,
    selectAllRegionEntities,
    (card, vehicles, controlZones, commCenters, regions) => {
      if (card == null) {
        return {};
      }
      const region =
        card.regionId != null ? regions.find((r) => r.id === card.regionId) : undefined;
      const commCenter =
        region?.communicationCenterId != null
          ? commCenters.find((c) => c.id === region.communicationCenterId)
          : undefined;
      const vehicle = card.vehicleId != null ? vehicles.get(card.vehicleId) : undefined;
      const controlZone =
        vehicle?.controlZoneId != null ? controlZones.get(vehicle.controlZoneId) : undefined;

      const ids: CardAccessibleItemEntityIds = {
        communicationCenterId: commCenter?.id,
        companyId: card.companyId,
        controlZoneId: controlZone?.id,
      };
      return ids;
    },
  );
}

function selectCardAccessibleItemEntityIds(cardId: string) {
  return createSelector(
    selectCardById(cardId),
    selectRegionByCardId(cardId),
    selectUnitSummaryByCardId(cardId),
    selectAllVehicleEntities,
    selectAllControlZoneEntities,
    selectAllCommunicationCenterEntities,
    (card, region, unit, vehicles, controlZones, commCenters) => {
      if (card == null || region == null) {
        return {};
      }
      const commCenter =
        region?.communicationCenterId != null
          ? commCenters.find((c) => c.id === region.communicationCenterId)
          : undefined;
      const vehicle = unit?.vehicleId != null ? vehicles.get(unit.vehicleId) : undefined;
      const controlZone =
        vehicle?.controlZoneId != null ? controlZones.get(vehicle.controlZoneId) : undefined;

      const ids: CardAccessibleItemEntityIds = {
        communicationCenterId: commCenter?.id,
        companyId: card.companyId,
        controlZoneId: controlZone?.id,
      };
      return ids;
    },
  );
}

@Injectable({ providedIn: 'root' })
export class CardAuthorizationService {
  constructor(
    private _user: UserService,
    private _permissions: PermissionsService,
    private _store: Store<AppState>,
    private _gql: FetchCardEntitiesGQL,
    private _storeService: StoreService,
  ) {}

  public isAuthorizedForCardNumber(cardNumber: string): Observable<boolean> {
    return this._store.select(selectCardByCardNumber(cardNumber)).pipe(
      valueOrSwitchMap(() =>
        this._storeService
          .upsertEntitiesFromGraphQL(this._gql, { cardNumber })
          .pipe(map((q) => q.data?.cards?.byCardNumber)),
      ),
      switchMap((card) => {
        const permissionsToCheck =
          card?.isLocked === true
            ? TcsCustomPermission.ViewLockedCards
            : [TcsCustomPermission.ManageOperations, TcsCustomPermission.ReadonlyOperations];
        return card == null ? of(false) : this.hasPermissionForCardId(card.id, permissionsToCheck);
      }),
    );
  }

  public hasPermission(
    cardAccessibleItemIds: CardAccessibleItemEntityIds,
    permission: TcsPermission | TcsPermission[],
  ): Observable<boolean> {
    const permissions = Array.isArray(permission) ? permission : [permission];
    return this._user.user$.pipe(
      switchMap((u) => {
        if (u == null) {
          return of(false);
        }
        return this._store
          .select(
            selectCardSummaryAccessibleItemsForUser({ userId: u.id, card: cardAccessibleItemIds }),
          )
          .pipe(
            map((ais) => {
              const t = ais.concat([u]);
              const roles = flatMapUserRoles(t);
              const hasPermission =
                permissions.find((p) => this._permissions.hasPermission(roles.toArray(), p)) !=
                null;
              return hasPermission;
            }),
          );
      }),
    );
  }

  public hasPermissionForCardSummaryId(
    cardId: string,
    permission: TcsPermission | TcsPermission[],
  ): Observable<boolean> {
    return this._store
      .select(selectCardSummaryAccessibleItemEntityIds(cardId))
      .pipe(switchMap((cai) => (cai == null ? of(false) : this.hasPermission(cai, permission))));
  }

  public hasPermissionForCardId(
    cardId: string,
    permission: TcsPermission | TcsPermission[],
  ): Observable<boolean> {
    return this._store
      .select(selectCardAccessibleItemEntityIds(cardId))
      .pipe(switchMap((cai) => (cai == null ? of(false) : this.hasPermission(cai, permission))));
  }
}
